import { Box, CircularProgress } from '@material-ui/core';
import { init } from '@nemuru-eng/checkout-js';
import { getLoanById, Loan, patchUpdateLoanConditionsByPricingIdPrincipalAndPeriod } from '@nemuru-eng/js-api-client';
import { useEffect, useState } from 'react';

import { GetPricingsFromCdnService } from '@/components/modules/Application/components/PaymentMethod/_shared/services/GetPricingsFromCdnService';
import { EditDialogWrapper } from '@/components/shared/components/EditDialogWrapper';
import { Font } from '@/components/shared/components/Font';
import { PendingDataWarning } from '@/components/shared/components/PendingDataWarning';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { ChangeLoanConditionsForm } from '@/components/widgets/LoanDetailActions/components/LoanDetailActionsDialog/components/ChangeLoanConditionsDialog/components/ChangeLoanConditionsForm';
import {
  ChangeLoanConditionsSchema,
  ChangeLoanConditionsType,
} from '@/components/widgets/LoanDetailActions/components/LoanDetailActionsDialog/schemas/ChangeLoanConditions.schema';
import { ENV_ENV } from '@/config/envVars';
import { PendingDataType } from '@/config/types';
import { useCache } from '@/hooks/requests/useCache';
import { useRequest } from '@/hooks/requests/useRequest';

const pricingsService = GetPricingsFromCdnService.getInstance();

interface ChangeLoanConditionsDialogProps {
  loan: Loan;
  opened: boolean;
  setOpened: (value: boolean) => void;
  handleClose: () => void;
}

export const ChangeLoanConditionsDialog = ({
  loan,
  opened,
  setOpened,
  handleClose,
}: ChangeLoanConditionsDialogProps) => {
  const { getTextIdTranslation } = useTranslation();
  const [loading, setLoading] = useState(false);
  const { request, loading: loadingRequest } = useRequest();
  const { revalidate, loading: revalidateLoading } = useCache(getLoanById, { loanId: loan.id });
  useEffect(() => {
    if (loan) {
      getPricingAndLoanConditions();
    }
  }, [loan]);

  const getPricingAndLoanConditions = async () => {
    setLoading(true);
    try {
      await init(loan.agentId, { env: ENV_ENV });
    } catch (error) {
      setLoading(false);
      throw new Error('failed to initialize checkout-js');
    }
    try {
      await pricingsService.execute(loan.agentId);
    } catch (error) {
      throw new Error('failed to get pricings from agent');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (formValues: ChangeLoanConditionsType) => {
    try {
      await request(patchUpdateLoanConditionsByPricingIdPrincipalAndPeriod, {
        id: loan.id,
        pricingId: formValues.pricingId,
        principal: Number(formValues.principal),
        period: Number(formValues.term),
      });
      await revalidate();
      handleClose();
    } catch (error) {}
  };

  return (
    <EditDialogWrapper
      opened={opened}
      setOpened={setOpened}
      handleClose={handleClose}
      dialogProps={{
        maxWidth: 'sm',
        fullWidth: true,
      }}
      title={'EDIT_LOAN_CONDITIONS'}
      isLoading={!!(loading && pricingsService.pricings) || loadingRequest || revalidateLoading}
      onSubmitForm={onSubmit}
      saveButtonTextId={'CHANGE_LOAN_CONDITIONS'}
      formSchema={() => ChangeLoanConditionsSchema(getTextIdTranslation, pricingsService.pricings)}
    >
      {loading ? (
        <Box display={'flex'} flexDirection={'row'} gridGap={8}>
          <CircularProgress color="secondary" size={16} />
          <Font variant="body2" color="textSecondary">
            <Text tId={'LOADING_PRICINGS'} />
            ...
          </Font>
        </Box>
      ) : !pricingsService.pricings ? (
        <PendingDataWarning type={PendingDataType.NO_PRICINGS_AVAILABLE} />
      ) : (
        <ChangeLoanConditionsForm loan={loan} pricings={pricingsService.pricings} />
      )}
    </EditDialogWrapper>
  );
};
